import { useEffect, useState } from 'react';
import { WhatsNew } from '@ui';
import { whatsNewConfig } from '../config';
import type { WhatsNewData } from './types';
import { loadWhatsNew } from './whatsNewApi';
import { guideTo, resumeGuide } from './placeGuide';

export interface WhatsNewScreenProps {
  /** Host endpoint; default — the service with `whatsNew.service`, else `whatsNew.path` of `configure()`. */
  path?: string;
  /** Read from the service even before `configure()` gave `whatsNew` (the host page mounts the screen at once). */
  service?: boolean;
  /** In-app navigation; default — `whatsNew.navigate`, else a plain page load. */
  navigate?: ((href: string) => void) | null;
  /** Search examples under the "Где это?" field. */
  examples?: string[];
}

/**
 * "Что нового" — a screen of its own, drawn by the library (`WhatsNew`, CRM release_notes Рн9): the widget only loads
 * the notes and «Где это?», and hands the click on a place over — the guide is remembered before the navigation (report
 * 35) and shown on the screen it opens.
 */
export default function WhatsNewScreen({ path, service, navigate, examples = [] }: WhatsNewScreenProps) {
  const setup = whatsNewConfig();
  const fromService = service === true || (path === undefined && setup?.service === true);
  const source = fromService ? '' : (path ?? setup?.path ?? '');
  const go = navigate === undefined ? (setup?.navigate ?? null) : navigate;
  const [data, setData] = useState<WhatsNewData | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!source && !fromService) {
      setError('Приложение не подключило заметки о выпусках.');
      return;
    }
    let alive = true;
    loadWhatsNew(fromService ? { service: true } : source).then(
      (result) => alive && setData(result),
      (err: unknown) => alive && setError(err instanceof Error ? err.message : 'Ошибка.'),
    );
    return () => {
      alive = false;
    };
  }, [source, fromService]);

  return (
    <WhatsNew
      notes={data?.notes ?? null}
      index={data?.index ?? []}
      error={error || undefined}
      examples={examples}
      navigate={go}
      onPlace={guideTo}
      onNavigated={(guide) => guide && resumeGuide()}
    />
  );
}
